/**
 * Session Lifecycle Manager
 *
 * Manages user sessions: start/end, AppState listeners (mobile),
 * visibility listeners (web). Each session gets a UUID and a row in user_sessions.
 *
 * A session row is analytics like any event, so it obeys the same consent gate
 * (see tracker.ts): the row is written only once the user's account flag
 * (profiles.analytics_opt_out) is known to allow it — immediately if it already
 * is, otherwise when consent is granted — and nothing (start or end) is written
 * for a user who opted out. This client gate is the ONLY enforcement for
 * user_sessions: the database trigger behind the flag covers analytics_events.
 * analytics_events.session_id references user_sessions, so the row MUST exist
 * before events carrying its id are flushed; recording it on grant guarantees
 * that for events held while the preference was loading.
 */

import { Platform, AppState } from 'react-native';
import { supabase } from '@/lib/supabase/client';
import type { Json } from '@/lib/supabase/types';
import { mode } from '@/lib/config/app-mode';
import { getDeviceInfo, getAppVersion } from '@/lib/feedback/device-info';
import { reportError } from '@/lib/errors';
import { analyticsTracker } from './tracker';

let currentSessionId: string | null = null;
let sessionStartTime: number | null = null;
let currentUserId: string | null = null;
/** Whether the current session's row was written (consent was granted). */
let sessionRecorded = false;

// Listener subscriptions for cleanup
let appStateSubscription: { remove: () => void } | null = null;
let visibilityHandler: (() => void) | null = null;

function generateUUID(): string {
  // Simple UUID v4 for client-side session IDs
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Write the user_sessions row for the CURRENT session, once, if allowed. Never rejects. */
async function recordCurrentSession(): Promise<void> {
  if (!currentSessionId || !currentUserId || sessionRecorded || !analyticsTracker.isEnabled()) return;
  sessionRecorded = true;

  if (mode.isMock) {
    console.debug('[Analytics] Session started:', currentSessionId);
    return;
  }
  try {
    const { error } = await supabase.from('user_sessions').insert({
      id: currentSessionId,
      user_id: currentUserId,
      platform: Platform.OS,
      app_version: getAppVersion(),
      device_info: getDeviceInfo() as unknown as Json,
    });
    if (error) throw error; // a resolved { error } is a failure too
  } catch (err) {
    reportError(err, { context: 'session.startSession', silent: true });
  }
}

// Consent granted after the session started (the account flag was still
// loading, or the user switched analytics back on): record the session now.
analyticsTracker.onConsentChange((consent) => {
  if (consent === 'granted') {
    void recordCurrentSession(); // never rejects: it reports its own failures
  }
});

/** Start a new session for the user */
export async function startSession(userId: string): Promise<void> {
  // End any existing session first
  if (currentSessionId && currentUserId) {
    await endSession(currentUserId);
  }

  currentUserId = userId;
  currentSessionId = generateUUID();
  sessionStartTime = Date.now();
  sessionRecorded = false;

  analyticsTracker.identify(userId);
  analyticsTracker.setSessionId(currentSessionId);
  await analyticsTracker.init();

  // Written now if consent is already known to allow it; otherwise on grant.
  await recordCurrentSession();

  // Register platform-specific listeners
  registerListeners(userId);
}

/** End the current session */
export async function endSession(userId: string): Promise<void> {
  if (!currentSessionId || userId !== currentUserId) return;

  const sessionId = currentSessionId;
  const wasRecorded = sessionRecorded;
  const durationSeconds = sessionStartTime
    ? Math.round((Date.now() - sessionStartTime) / 1000)
    : null;

  // Clear current session state
  currentSessionId = null;
  sessionStartTime = null;
  sessionRecorded = false;
  analyticsTracker.setSessionId(null);

  // Flush any pending events (a no-op unless consent is granted)
  await analyticsTracker.flush();

  // Never recorded, or the user opted out since: there is no row to close,
  // and an opted-out user's session must not be written to at all.
  if (!wasRecorded || !analyticsTracker.isEnabled()) return;

  if (mode.isMock) {
    console.debug('[Analytics] Session ended:', sessionId, `${durationSeconds}s`);
    return;
  }

  try {
    const { error } = await supabase
      .from('user_sessions')
      .update({
        ended_at: new Date().toISOString(),
        duration_seconds: durationSeconds,
        is_active: false,
      })
      .eq('id', sessionId);
    if (error) throw error; // a resolved { error } is a failure too
  } catch (err) {
    reportError(err, { context: 'session.endSession', silent: true });
  }
}

/** Clean up all listeners */
export function cleanupSessionListeners(): void {
  if (appStateSubscription) {
    appStateSubscription.remove();
    appStateSubscription = null;
  }

  if (visibilityHandler && Platform.OS === 'web' && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
}

/** Register AppState (mobile) or visibilitychange (web) listeners */
function registerListeners(userId: string): void {
  // Clean up any existing listeners first
  cleanupSessionListeners();

  if (Platform.OS === 'web') {
    if (typeof document !== 'undefined') {
      visibilityHandler = () => {
        if (document.visibilityState === 'hidden') {
          endSession(userId);
        } else if (document.visibilityState === 'visible' && !currentSessionId) {
          startSession(userId);
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);
    }
  } else {
    // Mobile: listen for app state changes
    appStateSubscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'background' || nextState === 'inactive') {
        endSession(userId);
      } else if (nextState === 'active' && !currentSessionId) {
        startSession(userId);
      }
    });
  }
}
